import { Component, Prop, h } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'sn-workflow-setup';

/**
 * Workflow setup comparison table for SignNow plan or feature breakdowns.
 * Full-width heading + subtitle on top, then a tabular layout with header row and alternating-background data rows.
 * Renders structured tabular data via native `<table>` markup, unlike card- or accordion-based SignNow blocks.
 *
 * @category SignNow
 * @slot heading - {content} <me-heading variant="4" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Section heading for workflow setup for {keyword}. 6-10 words.
 * @slot subtitle - {content} <me-text variant="large"> - Introductory paragraph for the workflow setup table. You MUST write 15-30 words.
 * @example
 *   <sn-workflow-setup
 *     columns='[{"title":"Functionality"},{"title":"Pricing"}]'
 *     items='[{"label":"Legally-binding eSignatures","value":"Included in all plans"},{"label":"Audit trails","value":"Included in all plans"},{"label":"Bulk send","value":"Business plans and above"}]'
 *   >
 *     <me-heading slot="heading" variant="4" tag="h2">Workflow setup</me-heading>
 *     <me-text slot="subtitle" variant="large">Understanding the key elements.</me-text>
 *   </sn-workflow-setup>
 */
@Component({
  tag: 'sn-workflow-setup',
  styleUrl: 'index.scss',
  shadow: false,
})
export class DpcWorkflowTable {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';
  /**
   * Table column headers.
   * @content
   * @type {array}
   * @item.type {object}
   * @item.property {string} title - Column header label
   * @item.required title
   * @item.content title
   * @minItems 2
   * @maxItems 2
   */
  @Prop({ mutable: true }) columns?: { title?: string }[] | string;

  /**
   * Table row data. Each item represents one row with a label (first column) and value (second column).
   * @content
   * @type {array}
   * @item.type {object}
   * @item.property {string} label - Setting or configuration option name. 5-10 words.
   * @item.property {string} value - Concise configuration value or recommendation. 5-15 words.
   * @item.required label
   * @item.required value
   * @item.content label value
   * @minItems 5
   * @maxItems 5
   */
  @Prop({ mutable: true }) items?: { label?: string; value?: string }[] | string;

  /**
   * Legacy / raw-HTML fallback. For new usage prefer slots + `columns` / `items` props.
   * @dynamic
   * @type {object}
   * @property {string} raw_html - Raw HTML content (replaces entire block)
   * @property {string} title - Fallback heading (use slot="heading" instead)
   * @property {string} subtitle - Fallback subtitle (use slot="subtitle" instead)
   * @property {array} headers - Fallback headers (use columns prop instead)
   * @property {array} rows - Fallback rows (use items prop instead)
   */
  @Prop({ mutable: true }) values?:
    | {
        title?: string;
        subtitle?: string;
        raw_html?: string;
        headers?: string[];
        rows?: string[][];
      }
    | string;

  componentWillLoad() {
    this.values = parseJsonProp(this.values);
    this.columns = parseJsonProp(this.columns);
    this.items = parseJsonProp(this.items);
  }

  render() {
    const values = typeof this.values === 'object' ? this.values : undefined;

    if (values?.raw_html) {
      return <me-section class={COMPONENT_NAME} yGap="md" yGapLg="xl" innerHTML={values.raw_html}></me-section>;
    }

    const columns = Array.isArray(this.columns)
      ? this.columns
      : (Array.isArray(values?.headers) ? values.headers.map((h) => ({ title: h })) : []);

    const items = Array.isArray(this.items)
      ? this.items
      : (Array.isArray(values?.rows) ? values.rows.map((r) => ({ label: r[0], value: r[1] })) : []);

    return (
      <me-section class={COMPONENT_NAME} yGap="3xl" yGapLg="5xl">
        <me-row direction="column" directionLg="column" yGapLg="none">
          <me-column spanLg="12">
            <div class={`${COMPONENT_NAME}__header`}>
              <slot name="heading">
                {values?.title && <me-heading variant="4" tag="h4">{values.title}</me-heading>}
              </slot>
              <slot name="subtitle">
                {values?.subtitle && <me-text variant="large">{values.subtitle}</me-text>}
              </slot>
            </div>
          </me-column>
        </me-row>
        {(columns.length > 0 || items.length > 0) && (
          <table class={`${COMPONENT_NAME}__table`}>
            {columns.length > 0 && (
              <thead>
                <tr class={`${COMPONENT_NAME}__row ${COMPONENT_NAME}__row--header`}>
                  {columns.map((col) => (
                    <th class={`${COMPONENT_NAME}__cell ${COMPONENT_NAME}__cell--header`}>{col.title}</th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {items.map((item) => (
                <tr class={`${COMPONENT_NAME}__row`}>
                  <td class={`${COMPONENT_NAME}__cell`}>{item.label}</td>
                  <td class={`${COMPONENT_NAME}__cell`}>{item.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </me-section>
    );
  }
}
